/**
 * Box 2 (spec §3.3): one row per creative, in the view's order, through D1's OverviewBox (loading, failed with
 * "ลองอีกครั้ง", the data) and OverviewTable (numbers right-aligned, an n<500 row faded, a wide table scrolling inside
 * its box). Columns: Creative · อายุ · Spend · Imp · Clicks · CTR · CPC · CPM · Result · Cost/Result · Engagement ·
 * View rate. No total line: Result cannot be summed across objectives. Over SHOW_FIRST rows, "แสดง N จาก M creative"
 * and the toggle; the footnotes close the box, also under an empty table (R12, R14).
 *
 * @module components/creatives/CreativeTable
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { BoxMessage } from '@/components/looker/app/appBoxShared';
import { OverviewBox } from '@/components/overview/OverviewBox';
import { OverviewTable, type OverviewColumn } from '@/components/overview/OverviewTable';
import { PlatformDot } from '@/components/PlatformDot';
import { CHANNEL_LABEL, type TFn } from '@/services/channels/channelFormat';
import { creativeFormatLabel } from '@/services/creatives/creativeFormat';
import {
  ageText, costPerResultText, creativeNameOf, emptyTextOf, engagementText, resultCellOf, type AppliedFilters,
} from '@/services/creatives/creativeText';
import { SHOW_FIRST, type CreativeRow, type CreativeTableData } from '@/services/creatives/creativeTypes';
import { fmtCount, fmtMoney, fmtPct } from '@/services/overview/overviewFormat';
import type { BoxState } from '@/services/overview/overviewTypes';
import { CreativeChips } from './CreativeChips';
import { CreativeThumb } from './CreativeThumb';

const TOGGLE_CLASS =
  'rounded-lg border border-neutral-300 bg-white px-3 py-1.5 text-sm font-medium text-ink-900 hover:bg-neutral-50 '
  + 'focus:outline-none focus:ring-2 focus:ring-ink-950 print:hidden';

/**
 * The thumbnail; the name on two lines at most (the whole name on hover, all of it on paper); the meta line, channel ·
 * format · campaign: one line on screen, where only the campaign gives way (an ellipsis, its whole name on hover and in
 * the CSV); on paper, which has no hover, the campaign takes its own lines in full (the owner's call); the chips.
 * 16rem wide on paper, so that with the page's print rules all twelve columns fit the card at the 1,040-px print frame
 * (spec §10 has the measurement).
 */
const CreativeCell: React.FC<{ row: CreativeRow }> = ({ row }) => {
  const { t } = useTranslation();
  const name = creativeNameOf(row, t);
  return (
    <div data-creative-cell className="flex w-72 items-start gap-3 whitespace-normal print:w-64">
      <CreativeThumb format={row.format} />
      <div className="min-w-0">
        <span data-creative-name title={name} className="line-clamp-2 break-words print:line-clamp-none">{name}</span>
        <span data-creative-meta className="mt-0.5 flex items-center gap-x-1.5 text-xs not-italic text-neutral-700 print:flex-wrap">
          <PlatformDot platform={row.platform} />
          <span className="shrink-0">{CHANNEL_LABEL[row.platform]}</span>
          <span aria-hidden="true" className="shrink-0">·</span>
          <span className="shrink-0">{creativeFormatLabel(row.format, t)}</span>
          <span aria-hidden="true" className="shrink-0 print:hidden">·</span>
          <span
            title={row.campaignName}
            className="min-w-0 truncate print:basis-full print:overflow-visible print:whitespace-normal print:break-words"
          >
            {row.campaignName}
          </span>
        </span>
        <CreativeChips row={row} />
      </div>
    </div>
  );
};

/** The figure, and under it the metric it counts (none under "—"). */
const ResultCell: React.FC<{ row: CreativeRow }> = ({ row }) => {
  const { t } = useTranslation();
  const cell = resultCellOf(row, t);
  return (
    <>
      <span className="block">{cell.value}</span>
      {cell.metric !== null && <span data-result-metric className="block text-xs not-italic text-neutral-700">{cell.metric}</span>}
    </>
  );
};

const columnsOf = (t: TFn, currency: string | null): Array<OverviewColumn<CreativeRow>> => {
  const num = (id: string, header: string, cell: (r: CreativeRow) => React.ReactNode): OverviewColumn<CreativeRow> => ({ id, header, numeric: true, cell });
  return [
    { id: 'creative', header: t('creatives.col.creative'), cell: (r) => <CreativeCell row={r} /> },
    num('age', t('creatives.col.age'), (r) => ageText(r.age, t)),
    num('spend', t('creatives.col.spend'), (r) => fmtMoney(r.spend, currency)),
    num('impressions', t('creatives.col.impressions'), (r) => fmtCount(r.impressions)),
    num('clicks', t('creatives.col.clicks'), (r) => fmtCount(r.clicks)),
    num('ctr', t('creatives.col.ctr'), (r) => fmtPct(r.ctr)),
    num('cpc', t('creatives.col.cpc'), (r) => fmtMoney(r.cpc, currency)),
    num('cpm', t('creatives.col.cpm'), (r) => fmtMoney(r.cpm, currency)),
    num('result', t('creatives.col.result'), (r) => <ResultCell row={r} />),
    num('costPerResult', t('creatives.col.costPerResult'), (r) => costPerResultText(r, currency, t)),
    num('engagement', t('creatives.col.engagement'), (r) => engagementText(r.engagement, t)),
    num('viewRate', t('creatives.col.viewRate'), (r) => fmtPct(r.viewRate)),
  ];
};

interface ShowingProps {
  shown: number;
  total: number;
  showAll: boolean;
  onShowAll: (on: boolean) => void;
}

/** "แสดง 15 จาก 40 creative" (printed) and the toggle (not printed). */
const Showing: React.FC<ShowingProps> = ({ shown, total, showAll, onShowAll }) => {
  const { t } = useTranslation();
  return (
    <div data-creative-showing className="mt-3 flex flex-wrap items-center gap-3 text-sm text-neutral-700">
      <span>{t('creatives.showing', { shown, total })}</span>
      <button type="button" className={TOGGLE_CLASS} onClick={() => onShowAll(!showAll)}>
        {showAll ? t('creatives.showTop', { n: SHOW_FIRST }) : t('creatives.showAll')}
      </button>
    </div>
  );
};

interface Props {
  state: BoxState<CreativeTableData>;
  /** The page currency (D1's choice); null until the facts are read. */
  currency: string | null;
  /** The workspace's name, or "ทุก workspace": the empty line names it (R12). */
  scope: string;
  filters: AppliedFilters;
  /** footnotesOf(…), in order. */
  notes: readonly string[];
  showAll: boolean;
  onShowAll: (on: boolean) => void;
  onRetry: () => void;
}

export const CreativeTable: React.FC<Props> = ({ state, currency, scope, filters, notes, showAll, onShowAll, onRetry }) => {
  const { t } = useTranslation();
  return (
    <OverviewBox id="2" title={t('creatives.box.table')} state={state} onRetry={onRetry} className="print:break-inside-auto">
      {(data) => (
        <>
          {data.rows.length === 0
            ? <BoxMessage text={emptyTextOf(data, scope, filters, t)} />
            : <OverviewTable columns={columnsOf(t, currency)} rows={data.shown} rowKey={(r) => r.key} faded={(r) => r.lowSample} />}
          {data.rows.length > SHOW_FIRST && <Showing shown={data.shown.length} total={data.rows.length} showAll={showAll} onShowAll={onShowAll} />}
          <ul data-creative-notes className="mt-3 list-disc space-y-1 pl-5 text-xs text-neutral-700">
            {notes.map((note) => <li key={note}>{note}</li>)}
          </ul>
        </>
      )}
    </OverviewBox>
  );
};
